/* The "thank you" confirmation page rendered after a vendor submits
   their application. Receives a snapshot of the submitted data so the
   vendor can review/print/screenshot it. */
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Printer, Plus } from "lucide-react";

const LOGO_URL = "/logo-transparent.png";

const ReceiptRow = ({ label, value }) => {
  const display = Array.isArray(value)
    ? value.filter(Boolean).join(", ")
    : value;
  if (!display && display !== 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-6 gap-y-1 py-2.5 border-b border-[var(--ld-border)]/60 last:border-b-0">
      <dt className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)] pt-1">
        {label}
      </dt>
      <dd className="text-[14px] text-[var(--ld-ink)] whitespace-pre-wrap break-words leading-[1.6]">
        {display}
      </dd>
    </div>
  );
};

export const SubmissionReceipt = ({ snapshot }) => {
  const submittedDate = snapshot.submittedAt || new Date();
  const niceDate = submittedDate.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const niceTime = submittedDate.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return (
    <main
      data-testid="vendor-apply-confirmation"
      className="relative min-h-[100dvh] w-full overflow-x-hidden print:bg-white"
      style={{ backgroundColor: "var(--ld-ivory)" }}
    >
      <header className="relative z-20 flex items-center justify-between gap-6 px-6 md:px-12 pt-5 md:pt-7 print:hidden">
        <Link
          to="/"
          data-testid="confirmation-back"
          className="inline-flex items-center gap-2 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.6} />
          <span className="font-accent text-[10px] tracking-[0.25em]">
            Back
          </span>
        </Link>
      </header>

      <section className="px-6 md:px-12 py-12 md:py-16">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <img
              src={LOGO_URL}
              alt=""
              aria-hidden
              className="mx-auto w-[100px] h-auto print:w-[80px]"
            />
            <div
              className="mx-auto mt-7 inline-flex items-center justify-center w-12 h-12 border border-[var(--ld-gold-deep)]/40 text-[var(--ld-gold-deep)]"
              data-testid="confirmation-check"
            >
              <CheckCircle2 size={20} strokeWidth={1.4} />
            </div>
            <span className="mt-5 inline-block font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
              Application received
            </span>
            <h1
              data-testid="confirmation-heading"
              className="font-serif-display mt-3 text-3xl sm:text-4xl md:text-[44px] text-[var(--ld-ink)] font-medium leading-tight"
            >
              Thank you, {snapshot.full_name?.split(" ")[0] || "vendor"}.
            </h1>
            <p className="mt-4 text-[15px] text-[var(--ld-ink-muted)] max-w-xl mx-auto leading-[1.7]">
              We&apos;ve received your application for{" "}
              <span className="text-[var(--ld-ink)]">
                {snapshot.company_name || "your company"}
              </span>
              . Our team will review your details and get back to you within{" "}
              <span className="text-[var(--ld-ink)]">3-5 business days</span>{" "}
              at <span className="text-[var(--ld-ink)]">{snapshot.email}</span>.
            </p>
            <div className="mx-auto mt-6 h-px w-20 bg-[var(--ld-gold)]/60" />
          </div>

          <div
            data-testid="confirmation-meta"
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 text-center"
          >
            <div className="border border-[var(--ld-border)]/80 px-4 py-4 bg-white">
              <span className="block font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
                Submitted on
              </span>
              <span className="block mt-2 text-[13px] text-[var(--ld-ink)]">
                {niceDate}
              </span>
              <span className="block mt-1 text-[12px] text-[var(--ld-ink-muted)]">
                {niceTime}
              </span>
            </div>
            <div className="border border-[var(--ld-border)]/80 px-4 py-4 bg-white">
              <span className="block font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
                Review window
              </span>
              <span className="block mt-2 text-[13px] text-[var(--ld-ink)]">
                3-5 business days
              </span>
              <span className="block mt-1 text-[12px] text-[var(--ld-ink-muted)]">
                We&apos;ll email {snapshot.email}
              </span>
            </div>
          </div>

          <section
            data-testid="confirmation-summary"
            className="mt-12 bg-white border border-[var(--ld-border)]/80 p-6 md:p-8"
          >
            <h2 className="font-serif-display text-2xl text-[var(--ld-ink)]">
              What you submitted
            </h2>
            <p className="mt-2 text-[13px] text-[var(--ld-ink-muted)] print:hidden">
              Take a screenshot or print this page for your records — we&apos;ll
              also keep a copy on file.
            </p>

            <h3 className="mt-8 font-accent text-[11px] tracking-[0.22em] uppercase text-[var(--ld-gold-deep)]">
              About you
            </h3>
            <dl className="mt-2">
              <ReceiptRow label="Full name" value={snapshot.full_name} />
              <ReceiptRow label="Company" value={snapshot.company_name} />
              <ReceiptRow label="Email" value={snapshot.email} />
              <ReceiptRow label="Contact" value={snapshot.contact_number} />
              <ReceiptRow
                label="Profile photo"
                value={snapshot.profile_picture_url ? "Uploaded" : "None"}
              />
            </dl>

            <h3 className="mt-8 font-accent text-[11px] tracking-[0.22em] uppercase text-[var(--ld-gold-deep)]">
              Services & coverage
            </h3>
            <dl className="mt-2">
              <ReceiptRow label="Services" value={snapshot.service_types} />
              <ReceiptRow label="Based in" value={snapshot.based_in} />
              <ReceiptRow label="Coverage" value={snapshot.coverage} />
            </dl>

            <h3 className="mt-8 font-accent text-[11px] tracking-[0.22em] uppercase text-[var(--ld-gold-deep)]">
              Pricing
            </h3>
            <dl className="mt-2">
              <ReceiptRow label="Structure" value={snapshot.pricing_structure} />
              <ReceiptRow label="Starting price" value={snapshot.starting_price} />
              <ReceiptRow
                label="Weddings completed"
                value={snapshot.weddings_completed}
              />
              <ReceiptRow label="Packages / add-ons" value={snapshot.packages_addons} />
            </dl>

            <h3 className="mt-8 font-accent text-[11px] tracking-[0.22em] uppercase text-[var(--ld-gold-deep)]">
              About your work
            </h3>
            <dl className="mt-2">
              <ReceiptRow label="Short intro" value={snapshot.short_intro} />
              <ReceiptRow label="Other details" value={snapshot.other_details} />
              <ReceiptRow
                label="Media uploaded"
                value={
                  snapshot.mediaCount > 0
                    ? `${snapshot.mediaCount} file${
                        snapshot.mediaCount === 1 ? "" : "s"
                      }`
                    : "None"
                }
              />
            </dl>

            <h3 className="mt-8 font-accent text-[11px] tracking-[0.22em] uppercase text-[var(--ld-gold-deep)]">
              Intro slot
            </h3>
            <dl className="mt-2">
              <ReceiptRow label="Preferred date" value={snapshot.intro_slot_date} />
              <ReceiptRow label="Preferred time" value={snapshot.intro_slot_time} />
            </dl>
          </section>

          <section className="mt-10 print:mt-6">
            <h3 className="font-accent text-[11px] tracking-[0.22em] uppercase text-[var(--ld-gold-deep)] text-center">
              What happens next
            </h3>
            <ol className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  n: "01",
                  title: "We review",
                  body: "Our team reads through your application and checks the details you've shared.",
                },
                {
                  n: "02",
                  title: "We're in touch",
                  body: `We'll email ${snapshot.email} within 3-5 business days with our decision and any follow-up questions.`,
                },
                {
                  n: "03",
                  title: "You go live",
                  body: "If approved, your profile is published on the directory and couples can discover you immediately.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="bg-white border border-[var(--ld-border)]/80 p-5"
                >
                  <span className="font-accent text-[10px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
                    {s.n}
                  </span>
                  <h4 className="font-serif-display text-lg text-[var(--ld-ink)] mt-1.5">
                    {s.title}
                  </h4>
                  <p className="mt-2 text-[13px] text-[var(--ld-ink-muted)] leading-[1.65]">
                    {s.body}
                  </p>
                </li>
              ))}
            </ol>
          </section>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-3 print:hidden">
            <button
              type="button"
              data-testid="confirmation-print"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 h-12 px-7 text-[12px] tracking-[0.2em] uppercase border border-[var(--ld-ink)]/40 text-[var(--ld-ink)] hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
            >
              <Printer size={14} strokeWidth={1.6} />
              Print / save as PDF
            </button>
            <button
              type="button"
              data-testid="confirmation-submit-another"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 h-12 px-7 text-[12px] tracking-[0.2em] uppercase border border-[var(--ld-ink)]/40 text-[var(--ld-ink)] hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
            >
              <Plus size={14} strokeWidth={1.6} />
              Submit another
            </button>
            <Link
              to="/"
              data-testid="confirmation-home"
              className="inline-flex items-center gap-2 h-12 px-7 text-[12px] tracking-[0.2em] uppercase bg-[var(--ld-ink)] text-[var(--ld-ivory)] hover:bg-[var(--ld-gold-deep)] transition-colors"
            >
              Return home
            </Link>
          </div>
        </div>
      </section>

      <footer className="px-6 md:px-12 py-8 text-center text-xs tracking-[0.2em] uppercase text-[var(--ld-ink-muted)] print:hidden">
        © {new Date().getFullYear()} · Luxé Directory
      </footer>
    </main>
  );
};
